import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'
import ProductsCombo from '@/widgets/ProductsCombo'
import ProductsService from '@/services/ProductsService'
import { emptyProductForm, getFormPrice } from '@/pages/system/Settings/components/ProductFormModal'

const inputClassName =
	'w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'

const labelClassName = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600'

const buildFormFromLine = ({ name = '', price = '', taxPercent = '' } = {}) => ({
	...emptyProductForm,
	name: name || '',
	price: price === 0 || price ? String(price) : '',
	taxPercent: taxPercent === 0 || taxPercent ? String(taxPercent) : '',
})

export default function AssignProductModal({
	isOpen,
	onClose,
	onSelect,
	isAssigning = false,
	infaktProductName = '',
	price = '',
	taxPercent = '',
}) {
	const { t } = useTranslation()
	const [formState, setFormState] = useState(emptyProductForm)
	const [selectedProductId, setSelectedProductId] = useState('')
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		if (!isOpen) return
		setFormState(
			buildFormFromLine({
				name: infaktProductName,
				price,
				taxPercent,
			})
		)
		setSelectedProductId('')
		setIsDirty(false)
		setError(null)
		setIsSaving(false)
	}, [isOpen, infaktProductName, price, taxPercent])

	if (!isOpen) return null

	const articleNumberOptions = [
		{ value: '', label: t('settingsPage.products.fields.article_numberNone') },
		{ value: '43', label: '43' },
		{ value: '113', label: '113' },
	]

	const selectedArticleNumberOption = formState.article_number
		? { value: String(formState.article_number), label: String(formState.article_number) }
		: articleNumberOptions[0]

	const handleFieldChange = (field, value) => {
		setFormState(prev => ({ ...prev, [field]: value }))
		if (selectedProductId) setIsDirty(true)
	}

	const handleExistingProductSelect = product => {
		if (!product) {
			setSelectedProductId('')
			setIsDirty(false)
			return
		}
		setSelectedProductId(product._id || product.id || '')
		setIsDirty(false)
		setFormState({
			name: product?.name ?? '',
			sku: product?.sku ?? '',
			article_number: product?.article_number ?? '',
			price: getFormPrice(product),
			taxPercent: product?.taxPercent ?? '',
		})
		setError(null)
	}

	const handleSubmit = async event => {
		event.preventDefault()
		if (isAssigning || isSaving) return

		const trimmedName = (formState.name || '').trim()
		if (!trimmedName) {
			setError(t('invoices.details.assignProductModal.invalidProduct'))
			return
		}

		setIsSaving(true)
		setError(null)

		const catalogPayload = {
			name: trimmedName,
			sku: formState.sku || '',
			article_number: formState.article_number || '',
			price: formState.price,
			taxPercent: formState.taxPercent,
		}

		try {
			if (selectedProductId) {
				if (isDirty) {
					const { data, error: updateError } = await ProductsService.updateProduct(
						selectedProductId,
						catalogPayload
					)
					if (updateError) {
						setError(updateError || t('invoices.details.assignProductModal.errorFallback'))
						return
					}
					await onSelect(data || { _id: selectedProductId, name: trimmedName })
					return
				}
				await onSelect({ _id: selectedProductId, name: trimmedName })
				return
			}

			const { data, error: createError } = await ProductsService.addProduct(catalogPayload)

			if (createError || !data) {
				setError(createError || t('invoices.details.assignProductModal.errorFallback'))
				return
			}

			await onSelect(data)
		} catch (err) {
			setError(err?.message || t('invoices.details.assignProductModal.errorFallback'))
		} finally {
			setIsSaving(false)
		}
	}

	const busy = isAssigning || isSaving

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
			<div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl">
				<div className="flex items-start justify-between border-b border-gray-200 p-4">
					<div>
						<h3 className="text-base font-semibold text-gray-900">
							{t('invoices.details.assignProductModal.title')}
						</h3>
						<p className="mt-1 text-sm text-gray-500">
							{t('invoices.details.assignProductModal.description')}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
						{t('invoices.details.assignProductModal.close')}
					</button>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4 p-4">
					<div>
						<label className={labelClassName}>
							{t('invoices.details.assignProductModal.selectExisting')}
						</label>
						<ProductsCombo
							selectedId={selectedProductId}
							selectedProductName={selectedProductId ? formState.name : ''}
							onChange={handleExistingProductSelect}
							allowCreate={false}
						/>
					</div>

					<div>
						<label className={labelClassName}>{t('settingsPage.products.fields.name')}</label>
						<input
							type="text"
							value={formState.name}
							required
							disabled={busy}
							onChange={e => handleFieldChange('name', e.target.value)}
							className={inputClassName}
						/>
					</div>

					<div className="grid gap-4 md:grid-cols-2">
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.sku')}</label>
							<input
								type="text"
								value={formState.sku}
								disabled={busy}
								onChange={e => handleFieldChange('sku', e.target.value)}
								className={inputClassName}
							/>
						</div>
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.price')}</label>
							<input
								type="number"
								step="0.01"
								value={formState.price}
								disabled={busy}
								onChange={e => handleFieldChange('price', e.target.value)}
								className={inputClassName}
							/>
						</div>
					</div>

					<div className="grid gap-4 md:grid-cols-2">
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.taxPercent')}</label>
							<input
								type="number"
								step="0.01"
								min="0"
								value={formState.taxPercent}
								disabled={busy}
								onChange={e => handleFieldChange('taxPercent', e.target.value)}
								className={inputClassName}
							/>
						</div>
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.article_number')}</label>
							<CreatableSelect
								name="article_number"
								options={articleNumberOptions}
								value={selectedArticleNumberOption}
								isDisabled={busy}
								onChange={option => handleFieldChange('article_number', option?.value ?? '')}
								onCreateOption={value => handleFieldChange('article_number', value)}
								placeholder={t('settingsPage.products.fields.article_numberPlaceholder')}
								formatCreateLabel={value =>
									t('settingsPage.products.fields.createArticleNumber', { value })
								}
								className="react-select"
								classNamePrefix="react-select"
							/>
						</div>
					</div>

					{error ? <p className="text-sm text-red-600">{error}</p> : null}

					<div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
						<button
							type="button"
							onClick={onClose}
							disabled={busy}
							className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-60">
							{t('notifications.cancelButton')}
						</button>
						<button
							type="submit"
							disabled={busy}
							className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:bg-indigo-300">
							{busy
								? t('invoices.details.assignProductModal.assigning')
								: selectedProductId
									? isDirty
										? t('invoices.details.assignProductModal.saveAndAssign')
										: t('invoices.details.assignProductModal.assignExisting')
									: t('invoices.details.assignProductModal.createAndAssign')}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
